// frontend/src/components/settings/ExactSupplierResolveDialog.jsx
//
// The Resolve wizard for an Exact ERP connector parked at RESOLVE.
//
// Exact's "suppliers" (crm/Accounts IsSupplier) include the tax office,
// banks, and the accountant — so before anything enters the planning model
// the merchant confirms WHICH suppliers to import. This dialog renders the
// staged rows (pulled, not committed) with:
//   * a per-row include checkbox, pre-ticked per the backend default
//     (active → on, inactive → off, already-paired → on)
//   * the suggested match against an existing Manexys supplier + confidence
//   * inactive / missing-data flags
// Confirming commits only the ticked suppliers and activates the connector.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import DialogErrorBanner from '../common/DialogErrorBanner';

import {
  useExactSupplierSelection,
  useExactConfirmSuppliers,
} from '../../hooks/useConnectorMutations';
import { translateRequestError } from '../../queries/api';

export default function ExactSupplierResolveDialog({ connectorId, open, onClose, onConfirmed }) {
  const { t } = useTranslation();
  const { data, isPending, error } = useExactSupplierSelection(connectorId, open);
  const confirmMut = useExactConfirmSuppliers(connectorId);

  // No seed effect: selection is the backend's per-row `default_selected`
  // with the merchant's explicit overrides layered on top, computed during
  // render. ``overrides`` maps external_id → boolean (only changed rows).
  // ``override` is keyed by connectorId so re-opening for another connector
  // (or after data reloads) starts clean without an effect.
  const [overrides, setOverrides] = useState({ key: null, map: {} });
  const [confirmError, setConfirmError] = useState(null);

  // Nothing to list until the selection has loaded.
  const suppliers = useMemo(() => (data ? data.suppliers : []), [data]);

  const ov = overrides.key === connectorId ? overrides.map : {};
  const isSelected = (s) => (s.external_id in ov ? ov[s.external_id] : !!s.default_selected);
  const selectedIds = suppliers.filter(isSelected).map((s) => s.external_id);
  const selectedCount = selectedIds.length;

  const setOverride = (id, value) => setOverrides((prev) => {
    const map = prev.key === connectorId ? { ...prev.map } : {};
    map[id] = value;
    return { key: connectorId, map };
  });
  const toggle = (s) => setOverride(s.external_id, !isSelected(s));

  const allOn = suppliers.length > 0 && selectedCount === suppliers.length;
  const toggleAll = () => setOverrides(() => ({
    key: connectorId,
    map: Object.fromEntries(suppliers.map((s) => [s.external_id, !allOn])),
  }));

  // Merge-on-pair: when a row has a suggested match, the merchant can opt to
  // merge into it (adopt the existing supplier) instead of creating a new
  // one. ``pairOptIn`` maps external_id → bool; only opted-in suggestions
  // become pairings. Keyed by connectorId, same no-effect pattern as above.
  const [pairOptIn, setPairOptIn] = useState({ key: null, map: {} });
  const pin = pairOptIn.key === connectorId ? pairOptIn.map : {};
  const isPaired = (s) => !!s.suggested_match_id && !!pin[s.external_id];
  const togglePair = (s) => setPairOptIn((prev) => {
    const map = prev.key === connectorId ? { ...prev.map } : {};
    map[s.external_id] = !map[s.external_id];
    return { key: connectorId, map };
  });

  const handleConfirm = async () => {
    setConfirmError(null);
    // Build pairings only for selected + opted-in rows with a suggestion.
    const pairings = {};
    for (const s of suppliers) {
      if (isSelected(s) && isPaired(s)) pairings[s.external_id] = s.suggested_match_id;
    }
    try {
      const resp = await confirmMut.mutateAsync({ selectedIds, pairings });
      onConfirmed?.(resp?.summary);
      onClose?.();
    } catch (err) {
      setConfirmError(translateRequestError(err, t, 'connectors.confirmFailed'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="dialog-form dialog-form--wide">
      <DialogTitle>{t('connectors.resolveTitle')}</DialogTitle>
      <DialogContent>
        <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {t('connectors.resolveHelp')}
        </p>

        {isPending ? (
          <div style={{ padding: 24, textAlign: 'center' }}><CircularProgress size={24} /></div>
        ) : error ? (
          <Alert severity="error">{translateRequestError(error, t, 'connectors.resolveLoadFailed')}</Alert>
        ) : suppliers.length === 0 ? (
          <Alert severity="info">
            {data?.gate_pending === false
              ? t('connectors.resolveEmptyConfirmed')
              : t('connectors.resolveEmpty')}
          </Alert>
        ) : (
          <>
            <FormControlLabel
              control={<Checkbox checked={allOn} indeterminate={selectedCount > 0 && !allOn} onChange={toggleAll} size="small" />}
              label={t('connectors.resolveSelectAll', { count: selectedCount, total: suppliers.length })}
            />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>{t('connectors.resolveColCode')}</TableCell>
                  <TableCell>{t('connectors.resolveColName')}</TableCell>
                  <TableCell>{t('connectors.resolveColCurrency')}</TableCell>
                  <TableCell>{t('connectors.resolveColMatch')}</TableCell>
                  <TableCell>{t('connectors.resolveColFlags')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.map((s) => (
                  <TableRow key={s.external_id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected(s)}
                        onChange={() => toggle(s)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{s.code || '—'}</TableCell>
                    <TableCell>{s.name || s.external_id}</TableCell>
                    <TableCell>{s.currency || '—'}</TableCell>
                    <TableCell>
                      {s.already_paired ? (
                        <Chip size="small" color="success" label={t('connectors.resolveAlreadyLinked')} />
                      ) : s.suggested_match_name ? (
                        <Chip
                          size="small"
                          clickable
                          variant={isPaired(s) ? 'filled' : 'outlined'}
                          color={isPaired(s) ? 'success' : (s.match_confidence === 'high' ? 'info' : 'default')}
                          onClick={() => togglePair(s)}
                          label={isPaired(s)
                            ? t('connectors.resolveMergeInto', { name: s.suggested_match_name })
                            : t('connectors.resolveMatchSuggestion', {
                                name: s.suggested_match_name,
                                confidence: s.match_confidence,
                              })}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {s.inactive && (
                        <Chip size="small" color="warning" label={t('connectors.resolveInactive')} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <DialogErrorBanner error={confirmError} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={confirmMut.isPending}>
          {t('btn.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isPending || suppliers.length === 0}
          loading={confirmMut.isPending}
        >
          {t('connectors.resolveConfirm', { count: selectedCount })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
